import React, { useMemo, useState, useRef, useEffect } from "react";
import { useUser } from "../../contexts/UserContext";
import { Link, useNavigate } from "react-router";
import LogoIcon from "../../assets/icons/latest_vivo_logo.svg?react";
import LogoutIcon from "../../assets/icons/logout.svg?react";
import { Layout, Button, Popconfirm } from "antd";
const { Header, Content } = Layout;

import authService from "../../services/authService";
import UserMenu from "../../components/UI/UserMenu";
import VivoProfileModal from "../../components/UI/VivoProfileModal";

import { getInitials } from "../../utils/UserInfoFormatting";

import { cleanTrialName } from "../../utils/stringUtils";
import { usePromotedTrials } from "../../hooks/usePromotedTrials";
import { useAdminFeatureAvailability } from "@/hooks/useAdminNavigation";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { usePortfolioLandingPath } from "@/hooks/usePortfolioLandingPath";

const NA = "N/A";

function unique(arr: (string | undefined | null)[]): string[] {
  const vals = arr.filter((v): v is string => v != null && v !== "");
  const sorted = [...new Set(vals)].sort();
  if (sorted.includes(NA)) return [...sorted.filter((v) => v !== NA), NA];
  return sorted;
}

function effectiveStatus(
  status: string | null | undefined,
  apiActive: boolean,
): string {
  if (status) return status;
  return apiActive ? "Active" : "Complete";
}

export default function RealHome() {
  const navigate = useNavigate();
  const landingPath = usePortfolioLandingPath();
  const { userInfo } = useUser();
  const { trials: promotedTrials, remove: removePromoted } =
    usePromotedTrials();
  const { hasAdminAccess } = useAdminFeatureAvailability();
  const trialCount = userInfo?.trials?.length ?? 0;
  // Trial planning is tenant-opt-in via the trial-planning feature flag.
  const hasPlanning =
    (userInfo?.available_trials as any[])?.some((t) =>
      t.feature_flags?.includes(FEATURE_FLAGS.TRIAL_PLANNING),
    ) ?? false;

  useEffect(() => {
    if (trialCount > 1) {
      navigate(landingPath, { replace: true });
    }
  }, [trialCount, navigate, landingPath]);

  const visibleTrials = useMemo(() => userInfo?.trials ?? [], [userInfo]);

  const [filterPhase, setFilterPhase] = useState<string | null>(null);
  const [filterIndication, setFilterIndication] = useState<string | null>(null);
  const [filterTA, setFilterTA] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const phaseOptions = useMemo(() => {
    const vals = [
      ...visibleTrials.map((t) => (t.phase != null ? `Phase ${t.phase}` : NA)),
      ...promotedTrials.map((t) => (t.phase ? t.phase : NA)),
    ];
    return unique(vals).map((v) => ({ label: v, value: v }));
  }, [visibleTrials, promotedTrials]);

  const indicationOptions = useMemo(() => {
    const vals = [
      ...visibleTrials.map((t) => t.indication ?? NA),
      ...promotedTrials.map((t) => t.indication ?? NA),
    ];
    return unique(vals).map((v) => ({ label: v, value: v }));
  }, [visibleTrials, promotedTrials]);

  const taOptions = useMemo(() => {
    const vals = [
      ...visibleTrials.map((t) => t.therapeutic_area ?? NA),
      ...promotedTrials.map((t) => t.therapeutic_area ?? NA),
    ];
    return unique(vals).map((v) => ({ label: v, value: v }));
  }, [visibleTrials, promotedTrials]);

  const statusOptions = useMemo(() => {
    const liveStatuses = visibleTrials.map((t) =>
      effectiveStatus(t.status, t.active ?? false),
    );
    const plannedStatuses = promotedTrials.map(() => "Planned");
    return unique([...liveStatuses, ...plannedStatuses]).map((v) => ({
      label: v,
      value: v,
    }));
  }, [visibleTrials, promotedTrials]);

  const filtersActive =
    filterPhase !== null ||
    filterIndication !== null ||
    filterTA !== null ||
    filterStatus !== null;

  const matchesFilters = (
    phase: string | null,
    indication: string | null | undefined,
    ta: string | null | undefined,
    status: string,
  ) => {
    if (filterPhase && filterPhase !== (phase ?? NA)) return false;
    if (filterIndication && filterIndication !== (indication ?? NA))
      return false;
    if (filterTA && filterTA !== (ta ?? NA)) return false;
    if (filterStatus && filterStatus !== status) return false;
    return true;
  };

  const filteredLive = visibleTrials.filter((t) =>
    matchesFilters(
      t.phase != null ? `Phase ${t.phase}` : null,
      t.indication,
      t.therapeutic_area,
      effectiveStatus(t.status, t.active ?? false),
    ),
  );

  const filteredPlanned = promotedTrials.filter((t) =>
    matchesFilters(
      t.phase || null,
      t.indication,
      t.therapeutic_area,
      "Planned",
    ),
  );

  return (
    <div data-testid="trial-selector-screen">
      <VivoProfileModal />
      <Layout className="main-auth-container">
        <Header className="bg-transparent h-[80px]">
          <div className="flex text-white items-center h-full justify-between mt-[7px]">
            <Link
              to="/home"
              className="flex items-center opacity-90 hover:opacity-100 transition-opacity"
            >
              <LogoIcon />
            </Link>
            <UserMenu>
              <div className="bg-[#0E98D0] rounded-full h-[40px] w-[40px] flex justify-center items-center text-md font-bold cursor-pointer">
                <span className="text-white">
                  {getInitials(userInfo?.first_name ?? "")[0]}
                </span>
              </div>
            </UserMenu>
          </div>
        </Header>

        <Content className="content-auth-container px-[70px] mt-[50px] pb-[70px]">
          {/* Heading row */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1
              className="text-white text-[32px]"
              data-testid="trial-selector-heading"
            >
              My Trials
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              {hasAdminAccess && (
                <Link
                  to="/admin"
                  data-testid="my-trials-admin-cta"
                  className="shrink-0"
                >
                  <Button
                    type="default"
                    className="shadow-none"
                    style={{
                      width: 118,
                      height: 32,
                      borderRadius: 4,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      background: "#fff",
                      color: "#2b4752",
                      fontFamily: "Work Sans, sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: "-0.14px",
                      lineHeight: "14px",
                      padding: 8,
                      boxShadow: "none",
                    }}
                  >
                    Admin Settings
                  </Button>
                </Link>
              )}
              {trialCount > 1 && hasPlanning && (
                <Link
                  to="/planning"
                  data-testid="my-trials-planning-cta"
                  className="shrink-0"
                >
                  <Button
                    type="default"
                    className="bg-transparent text-white font-bold text-[14px] border-white shadow-none hover:bg-transparent hover:text-white hover:border-white"
                  >
                    Trial Planning
                  </Button>
                </Link>
              )}
              {trialCount > 1 && (
                <Link
                  to={landingPath}
                  data-testid="my-trials-portfolio-cta"
                  className="shrink-0"
                >
                  <Button
                    type="primary"
                    className="bg-[#0C98D0] text-white font-bold text-[14px] border-none shadow-none"
                  >
                    Clinical Portfolio
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-2 mt-8 flex-wrap">
            <FilterSelect
              value={filterPhase}
              onChange={setFilterPhase}
              placeholder="Phase"
              options={phaseOptions}
            />
            <FilterSelect
              value={filterTA}
              onChange={setFilterTA}
              placeholder="Therapeutic Area"
              options={taOptions}
            />
            <FilterSelect
              value={filterIndication}
              onChange={setFilterIndication}
              placeholder="Indication"
              options={indicationOptions}
            />
            <FilterSelect
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Status"
              options={statusOptions}
            />
            {filtersActive && (
              <button
                type="button"
                onClick={() => {
                  setFilterPhase(null);
                  setFilterIndication(null);
                  setFilterTA(null);
                  setFilterStatus(null);
                }}
                className="text-white/50 hover:text-white/80 text-[13px] ml-1 transition-colors"
              >
                Clear
              </button>
            )}
            {filtersActive && (
              <span className="text-white/35 text-[12px]">
                {filteredLive.length + filteredPlanned.length} of{" "}
                {visibleTrials.length + promotedTrials.length}
              </span>
            )}
          </div>

          {/* Cards */}
          <div className="mt-[32px] grid grid-cols-3 gap-6 items-stretch">
            {filteredLive.map((trial) => (
              <TrialCard key={trial.id} trialInfo={trial} />
            ))}
            {filteredPlanned.map((pt) => (
              <PlannedTrialCard
                key={pt.planId}
                trial={pt}
                onRemove={removePromoted}
              />
            ))}
          </div>

          {filtersActive &&
            filteredLive.length === 0 &&
            filteredPlanned.length === 0 && (
              <p className="text-white/40 text-[14px] mt-8">
                No trials match the selected filters.
              </p>
            )}
        </Content>
      </Layout>
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  placeholder: string;
  options: { label: string; value: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = options.find((o) => o.value === value)?.label ?? null;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={[
          "flex items-center gap-1.5 h-[32px] px-3 rounded text-[13px] font-medium",
          "border transition-all duration-150 select-none",
          value
            ? "bg-[#0C98D0]/20 border-[#0C98D0]/60 text-white pr-1.5"
            : "bg-[rgba(9,75,104,0.45)] border-[rgba(100,189,225,0.25)] text-white/50 hover:border-[rgba(12,152,208,0.55)] hover:bg-[rgba(9,75,104,0.6)]",
          open
            ? "border-[#0C98D0] bg-[rgba(9,75,104,0.65)] shadow-[0_0_0_2px_rgba(12,152,208,0.2)]"
            : "",
        ].join(" ")}
      >
        <span className={value ? "text-white" : ""}>
          {selectedLabel ?? placeholder}
        </span>
        {value ? (
          <span
            role="button"
            aria-label="Clear filter"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
            className="ml-1 flex items-center justify-center w-4 h-4 rounded-full text-white/60 hover:text-white hover:bg-white/15 text-[11px] leading-none transition-colors"
          >
            ×
          </span>
        ) : (
          <svg
            className="w-3 h-3 text-[#64BDE1]/60 shrink-0 transition-transform duration-150"
            style={{ transform: open ? "rotate(180deg)" : "none" }}
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-50 min-w-full rounded overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
          style={{
            background: "rgba(18,52,68,0.97)",
            border: "1px solid rgba(100,189,225,0.18)",
          }}
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={[
                "w-full text-left px-3 py-2 text-[13px] font-medium transition-colors duration-100 whitespace-nowrap",
                o.value === value
                  ? "bg-[rgba(12,152,208,0.25)] text-[#64BDE1]"
                  : "text-white/80 hover:bg-[rgba(12,152,208,0.12)] hover:text-white",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TrialCard = ({ trialInfo }: { trialInfo: any }) => {
  const trial = trialInfo.id;
  const trialName = cleanTrialName(trialInfo.name ?? trialInfo.id);
  const trialData = trialInfo;

  return (
    <Link
      to={`/trial/${trial}`}
      key={trial}
      className="h-full block"
      data-testid="trial-selector-card"
    >
      <div className="p-[20px] bg-[rgba(0,0,0,0.48)] w-full h-full text-align-left rounded-[8px]">
        <div className="flex flex-col h-full justify-between">
          <div>
            <h2 className="text-[#0C98D0] text-[18px] font-bold">
              {trialName} (Phase {trialData.phase})
            </h2>
            <h3 className="text-white font-semibold text-[14px]">
              Sponsor: {trialData.sponsor}
            </h3>
            <p className="text-white text-[14px] mt-[5px] leading-6 line-clamp-3">
              {trialData.description}
            </p>
          </div>
          <div className="flex justify-end mt-5">
            <Button
              type="primary"
              className={`w-[94px] font-bold leading-6 text-[14px] text-center border-none shadow-none pointer-events-none ${
                effectiveStatus(trialData.status, trialData.active ?? false) ===
                "Active"
                  ? "bg-[#D6EEFA] text-[#0C5A8A]"
                  : "bg-[#CAE6D6] text-[#004A42]"
              }`}
            >
              {effectiveStatus(trialData.status, trialData.active ?? false)}
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

const PlannedTrialCard = ({
  trial,
  onRemove,
}: {
  trial: any;
  onRemove: any;
}) => (
  <div className="relative h-full group">
    <Link to={`/planned/${trial.planId}/home`} className="w-full h-full block">
      <div className="p-[20px] bg-[rgba(0,0,0,0.48)] w-full h-full text-align-left rounded-[8px] border border-[#8C2760]/40">
        <div className="flex flex-col h-full justify-between">
          <div>
            <h2 className="text-[#8C2760] text-[18px] font-bold">
              {trial.name} {trial.phase ? `(${trial.phase})` : ""}
            </h2>
            {trial.sponsor && (
              <h3 className="text-white font-semibold text-[14px] mt-0.5">
                Sponsor: {trial.sponsor}
              </h3>
            )}
            {trial.description && (
              <p className="text-white text-[14px] mt-[5px] leading-6 line-clamp-3">
                {trial.description}
              </p>
            )}
          </div>
          <div className="flex justify-end mt-5">
            <Popconfirm
              title="Remove from My Trials?"
              description="The plan stays intact in Trial Planning."
              okText="Remove"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={(e) => {
                e?.stopPropagation();
                onRemove(trial.planId);
              }}
              onPopupClick={(e) => e.stopPropagation()}
            >
              <Button
                type="primary"
                className="w-[94px] bg-[#8C2760] text-white font-bold leading-6 text-[14px] text-center border-none shadow-none hover:!bg-[#8C2760] hover:!text-white"
                onClick={(e) => e.preventDefault()}
              >
                Planned
              </Button>
            </Popconfirm>
          </div>
        </div>
      </div>
    </Link>
  </div>
);

/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
const FloatingLogoutButton = () => {
  return (
    <div
      className="userinfo-wrapper flex mb-2 gap-3 p-2"
      onClick={handleLogout}
    >
      <LogoutIcon />
      <p className="text-white">Logout</p>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const handleLogout = () => {
  authService.logout();
};
